// Draft load + auto-save hook.
//
// Lifecycle:
//   1. Mount: GET /api/drafts/:id → store full draft + last-known updated_at.
//   2. Edits mutate local state immediately and schedule a debounced PUT.
//   3. Each successful PUT echoes a new `updated_at` we use as the
//      `expected_updated_at` on subsequent saves.
//   4. If the server 409s (someone else edited), we surface a `conflict`
//      flag and stop writing until the user reloads.

import { useCallback, useEffect, useRef, useState } from 'react';
import { draftsApi, ApiError } from '../lib/api';
import type { Draft, Block, DraftStatus } from '@shared/types';
import { normalizeBlocks } from './normalize-blocks';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

const SAVE_DEBOUNCE_MS = 600;

export function useDraft(id: string | undefined) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // The latest `updated_at` the server has acknowledged. Used both for
  // optimistic concurrency on the next save and to refresh local state
  // after a save so further edits don't trip 409.
  const ackedUpdatedAtRef = useRef<number | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const pendingPayloadRef = useRef<{
    name?: string;
    subject?: string | null;
    preheader?: string | null;
    blocks?: Block[];
    status?: DraftStatus;
  } | null>(null);

  // Initial load
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    draftsApi
      .get(id)
      .then(({ draft }) => {
        if (cancelled) return;
        const normalized = { ...draft, blocks: normalizeBlocks(draft.blocks) };
        setDraft(normalized);
        ackedUpdatedAtRef.current = normalized.updated_at;
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e instanceof Error ? e.message : 'Failed to load draft');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Internal: actually flush the pending write to the server.
  const flush = useCallback(async () => {
    if (!id) return;
    if (saveStatus === 'conflict') return;

    // Coalesce: if there's an in-flight write, await it, then re-flush.
    if (inFlightRef.current) {
      await inFlightRef.current;
    }
    const payload = pendingPayloadRef.current;
    if (!payload) return;
    pendingPayloadRef.current = null;

    const expected = ackedUpdatedAtRef.current;
    if (expected === null) return;

    setSaveStatus('saving');
    const promise = (async () => {
      try {
        const result = await draftsApi.update(id, { ...payload, expected_updated_at: expected });
        ackedUpdatedAtRef.current = result.updated_at;
        // Update local state's `updated_at` so consumers see the freshness.
        setDraft((d) => (d ? { ...d, updated_at: result.updated_at } : d));
        setSaveStatus('saved');
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          setSaveStatus('conflict');
        } else {
          console.error('Save failed:', e);
          setSaveStatus('error');
        }
      } finally {
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    await promise;

    // If new edits piled up while we were saving, flush again. The next
    // entry to flush() will short-circuit if we hit a conflict state.
    if (pendingPayloadRef.current) {
      void flush();
    }
  }, [id, saveStatus]);

  const scheduleSave = useCallback(
    (patch: typeof pendingPayloadRef.current) => {
      pendingPayloadRef.current = { ...(pendingPayloadRef.current || {}), ...(patch || {}) };
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        saveTimerRef.current = null;
        void flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // Mutators — all of these update local state synchronously and queue a save.
  const updateName = useCallback((name: string) => {
    setDraft((d) => (d ? { ...d, name } : d));
    scheduleSave({ name });
  }, [scheduleSave]);

  const updateSubject = useCallback((subject: string | null) => {
    setDraft((d) => (d ? { ...d, subject } : d));
    scheduleSave({ subject });
  }, [scheduleSave]);

  const updatePreheader = useCallback((preheader: string | null) => {
    setDraft((d) => (d ? { ...d, preheader } : d));
    scheduleSave({ preheader });
  }, [scheduleSave]);

  const setBlocks = useCallback((blocks: Block[]) => {
    setDraft((d) => (d ? { ...d, blocks } : d));
    scheduleSave({ blocks });
  }, [scheduleSave]);

  const updateStatus = useCallback((status: DraftStatus) => {
    setDraft((d) => (d ? { ...d, status } : d));
    scheduleSave({ status });
  }, [scheduleSave]);

  // Force-flush helper — used before navigation, before export, etc.
  const flushNow = useCallback(async () => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await flush();
  }, [flush]);

  // Re-fetch the server's authoritative copy on conflict (clears the flag).
  const reload = useCallback(async () => {
    if (!id) return;
    pendingPayloadRef.current = null;
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaveStatus('idle');
    setLoading(true);
    try {
      const { draft } = await draftsApi.get(id);
      const normalized = { ...draft, blocks: normalizeBlocks(draft.blocks) };
      setDraft(normalized);
      ackedUpdatedAtRef.current = normalized.updated_at;
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Cleanup on unmount: clear any pending timer (don't auto-flush; the user
  // can navigate freely and the next mount picks up the server state).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    draft,
    loading,
    loadErr,
    saveStatus,
    updateName,
    updateSubject,
    updatePreheader,
    setBlocks,
    updateStatus,
    flushNow,
    reload,
  };
}
